import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type { Printer, PrinterInput, PrinterListParams } from '@/domain/entities/printer.entity'
import type { PrinterRepository } from '@/domain/repositories/printer.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'

export interface PrinterDto {
  id: string
  outlet_id: string
  name: string
  printer_type: string
  address: string
  paper_size: string
  is_cashier: boolean
  is_checker: boolean
  is_kitchen: boolean
  is_multiple_cashier: boolean
  status: boolean
  categories?: { id: string; name: string }[]
  created_at: string
  updated_at: string
}

function toEntity(dto: PrinterDto): Printer {
  const categories = dto.categories ?? []
  return {
    id: dto.id,
    outletId: dto.outlet_id,
    name: dto.name,
    printerType: dto.printer_type as Printer['printerType'],
    address: dto.address,
    paperSize: dto.paper_size as Printer['paperSize'],
    isCashier: dto.is_cashier,
    isChecker: dto.is_checker,
    isKitchen: dto.is_kitchen,
    isMultipleCashier: dto.is_multiple_cashier,
    status: dto.status,
    categoryIds: categories.map((c) => c.id),
    categories,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  }
}

export class PrinterHttpRepository implements PrinterRepository {
  async list(outletId: string, params?: PrinterListParams): Promise<Paged<Printer>> {
    const raw = await httpClient.get<unknown>('/printers', {
      params: {
        outlet_id: outletId,
        page: params?.page,
        limit: params?.limit,
        search: params?.search || undefined,
      },
    })
    const paged = unwrapPaged<PrinterDto>(raw, params?.page ?? 1, params?.limit ?? 20)
    return { ...paged, items: paged.items.map(toEntity) }
  }

  async getById(outletId: string, id: string): Promise<Printer> {
    const raw = await httpClient.get<unknown>(`/printers/${id}`, {
      params: { outlet_id: outletId },
    })
    return toEntity(unwrap<PrinterDto>(raw))
  }

  async create(outletId: string, input: PrinterInput): Promise<Printer> {
    const raw = await httpClient.post<unknown>('/printers', {
      outlet_id: outletId,
      name: input.name,
      printer_type: input.printerType,
      address: input.address,
      paper_size: input.paperSize,
      is_cashier: input.isCashier,
      is_checker: input.isChecker,
      is_kitchen: input.isKitchen,
      is_multiple_cashier: input.isMultipleCashier,
      category_ids: input.categoryIds,
    })
    return toEntity(unwrap<PrinterDto>(raw))
  }

  async update(outletId: string, id: string, input: PrinterInput): Promise<Printer> {
    const raw = await httpClient.put<unknown>(
      `/printers/${id}`,
      {
        name: input.name,
        printer_type: input.printerType,
        address: input.address,
        paper_size: input.paperSize,
        is_cashier: input.isCashier,
        is_checker: input.isChecker,
        is_kitchen: input.isKitchen,
        is_multiple_cashier: input.isMultipleCashier,
        category_ids: input.categoryIds,
      },
      {
        params: { outlet_id: outletId },
      },
    )
    return toEntity(unwrap<PrinterDto>(raw))
  }

  async delete(outletId: string, id: string): Promise<void> {
    await httpClient.delete(`/printers/${id}`, {
      params: { outlet_id: outletId },
    })
  }

  async toggleStatus(outletId: string, id: string, status: boolean): Promise<Printer> {
    const raw = await httpClient.patch<unknown>(
      `/printers/${id}/toggle-status`,
      { status },
      { params: { outlet_id: outletId } },
    )
    return toEntity(unwrap<PrinterDto>(raw))
  }
}
